import { useState, useEffect, useCallback } from 'react';
import { Worker } from '../types';

const STORAGE_KEY = 'skillhire-favorites';
const SHARE_KEY = 'skillhire-shared';

interface FavoritesState {
  workerIds: string[];
  workers: Worker[];
}

export function useFavorites(allWorkers: Worker[]) {
  const [favorites, setFavorites] = useState<FavoritesState>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const ids: string[] = JSON.parse(raw);
        return {
          workerIds: ids,
          workers: allWorkers.filter((w) => ids.includes(w.id) && w.status === 'published'),
        };
      }
    } catch {
      // ignore corrupted data
    }
    return { workerIds: [] as string[], workers: [] as Worker[] };
  });

  // Sync workers when allWorkers changes
  useEffect(() => {
    setFavorites((prev) => ({
      ...prev,
      workers: allWorkers.filter(
        (w) => prev.workerIds.includes(w.id) && w.status === 'published'
      ),
    }));
  }, [allWorkers]);

  const isFavorited = useCallback(
    (workerId: string) => favorites.workerIds.includes(workerId),
    [favorites.workerIds]
  );

  const toggleFavorite = useCallback(
    (workerId: string) => {
      setFavorites((prev) => {
        let newIds: string[];
        if (prev.workerIds.includes(workerId)) {
          newIds = prev.workerIds.filter((id) => id !== workerId);
        } else {
          newIds = [...prev.workerIds, workerId];
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newIds));
        return {
          workerIds: newIds,
          workers: allWorkers.filter(
            (w) => newIds.includes(w.id) && w.status === 'published'
          ),
        };
      });
    },
    [allWorkers]
  );

  const generateShareLink = useCallback(() => {
    const encoded = btoa(JSON.stringify(favorites.workerIds));
    const url = `${window.location.origin}${window.location.pathname}#/shared/${encoded}`;
    return url;
  }, [favorites.workerIds]);

  const parseSharedList = useCallback(
    (encoded: string): Worker[] => {
      try {
        const ids: string[] = JSON.parse(atob(encoded));
        return allWorkers.filter((w) => ids.includes(w.id) && w.status === 'published');
      } catch {
        return [];
      }
    },
    [allWorkers]
  );

  return {
    favorites: favorites.workers,
    favoriteIds: favorites.workerIds,
    isFavorited,
    toggleFavorite,
    generateShareLink,
    parseSharedList,
  };
}